import { KeyValueRow } from '@/components/ui/label/label-key-value-row';
import { KeyValueRowProvider } from '@/components/ui/label/label-key-value-row';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import {
  useGetServerlessTaskSpotById,
  useStopServerlessTaskSpot,
  useUpdateServerlessTaskSpot,
} from '@/service/serverless-gpu/hook';
import { PencilIcon, StopCircleIcon } from 'lucide-react';
import { LogsIcon } from 'lucide-react';
import { fCurrencyUSDExtended, fCurrencyUSDUnlimited } from '@/utils/formatNumber';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import FormTaskAdjustSpot from './FormTaskAdjustSpot';
import { useState } from 'react';
import { toast } from 'sonner';
import { StatusServerlessTask } from '@/service/serverless-gpu/type';

type Props = {
  idTask: string;
  status: StatusServerlessTask;
};
const TaskDetailSpot = ({ idTask, status }: Props) => {
  const { data: dataSpot, isLoading: isLoadingSpot } =
    useGetServerlessTaskSpotById({ task_id: idTask });

  const handleStopSpot = useStopServerlessTaskSpot();

  const dataSpotFormatted = {
    max_spending:
      !dataSpot ? '-' :
      dataSpot.max_spending === -1
        ? 'Unlimited'
        : fCurrencyUSDUnlimited(dataSpot.max_spending ?? 0),
  };

  return (
    <div className="mt-8 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogsIcon className="h-4 w-4" />
          <p className="text-lg font-medium text-foreground">Spot</p>
        </div>
        {[StatusServerlessTask.Pending, StatusServerlessTask.Running, StatusServerlessTask.WaitingForResource].includes(status) && (
          <div className="flex items-center gap-2">
            <DialogTaskAdjustSpot idTask={idTask} />
            <ConfirmationDialog
              title="Stop Spot"
            message="Are you sure you want to stop the spot?"
            onConfirm={() => {
              handleStopSpot.mutate({ task_id: idTask });
            }}
            confirmText="Stop"
            slots={{
              confirmButton: {
                variant: 'destructive',
              },
            }}
          >
            <Button
              variant={'destructive'}
              startIcon={<StopCircleIcon className="h-4 w-4" />}
              disabled={handleStopSpot.isPending}
            >
              Stop
            </Button>
            </ConfirmationDialog>
          </div>
        )}
      </div>
      <Separator className="mb-4 mt-2" />

      <KeyValueRowProvider isLoading={isLoadingSpot}>
        <div className="flex flex-col gap-3 text-start">
          <KeyValueRow
            label="Budget limit"
            value={dataSpotFormatted?.max_spending}
          />
        </div>
      </KeyValueRowProvider>
    </div>
  );
};

type TaskDetailSpotProps = {
  idTask: string;
};
export default TaskDetailSpot;

const DialogTaskAdjustSpot = ({ idTask }: TaskDetailSpotProps) => {
  const [open, setOpen] = useState(false);
  const handleAdjustSpot = useUpdateServerlessTaskSpot({
    onSuccess: () => {
      setOpen(false);
      toast.success('Spot adjusted successfully');
    },
    onError: () => {
      toast.error('Failed to adjust spot');
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={'outline'}
          startIcon={<PencilIcon className="h-4 w-4" />}
          disabled={handleAdjustSpot.isPending}
        >
          adjust
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Spot</DialogTitle>
        </DialogHeader>
        <FormTaskAdjustSpot
          onSubmit={(e) =>
            handleAdjustSpot.mutate({
              task_id: idTask,
              max_spending: e.max_spending,
            })
          }
        >
          <DialogFooter>
            <DialogClose asChild>
              <Button variant={'outline'}>Cancel</Button>
            </DialogClose>

            <Button type="submit" disabled={handleAdjustSpot.isPending}>
              Submit
            </Button>
          </DialogFooter>
        </FormTaskAdjustSpot>
      </DialogContent>
    </Dialog>
  );
};
